import classnames from 'owa-classnames';
import { customScrollBar } from 'owa-global-styles';
import { AccNetworkLostToast } from 'accelerator-common/lib/AccNetworkLostToast';
import { getCurrentPage } from 'hybridspace-common/lib/utils/getCurrentPage';
import loc from 'owa-localize';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { SkipLinkRegion } from 'owa-skip-link/lib/components/SkipLinkRegion';
import { type UTPageType } from 'owa-unified-telemetry';
import { isPlacesPremiumEnabled } from 'places-settings';
import React from 'react';
import { Outlet, useFetcher, useLocation, useNavigation, useRevalidator } from 'react-router-dom';
import { mergeClasses, Spinner } from '@fluentui/react-components';
import { placesMainContent, placesSkipToMainContent } from './PlacesRootRouteLayout.locstring.json';
import {
    exploreOutletContainer,
    loadingBackground,
    outletContainer as outletContainer_withoutCustomScrollBar,
    spinner,
} from './PlacesRootRouteOutletContainer.scss';
const outletContainer = classnames(customScrollBar, outletContainer_withoutCustomScrollBar);

let prefetchedPeople = false;
let prefetchedExplore = false;

export function PlacesRootRouteOutletContainer(): JSX.Element {
    const navigation = useNavigation();
    const revalidator = useRevalidator();
    const isLoading = navigation.state === 'loading' || revalidator.state != 'idle';

    // Tab Prefetching
    // Will only prefetch if the user is premium and the data has not already been prefetched
    // Prefetching only gets applied to Explore and People pages
    const location = useLocation();
    const mailboxInfo = getModuleContextMailboxInfo();
    const fetcher = useFetcher();
    const currentPage = getCurrentPage(window.location.pathname);
    const isCurrentPageExplore = currentPage === 'Explore';
    React.useEffect(() => {
        const isPremiumUser = isPlacesPremiumEnabled(mailboxInfo);

        // Skip prefetching if the user is core or if data has already been prefetched
        if (!isPremiumUser || (prefetchedExplore && prefetchedPeople)) {
            return;
        }

        // Only try to prefetch if the fetcher is idle
        if (fetcher.state === 'idle') {
            switch (currentPage) {
                case 'Explore':
                    if (!prefetchedExplore) {
                        prefetchedExplore = true; // If our current page is Explore, we know we no longer need to prefetch it
                        fetcher.load('/people?throwError=false'); // Prefetch workgroups loader
                        fetcher.load('/people?index&throwError=false'); // Prefetch workplan loader
                        prefetchedPeople = true; // Mark People as prefetched after we call fetcher
                    }
                    break;
                case 'People':
                    if (!prefetchedPeople) {
                        prefetchedPeople = true; // If our current page is People, we know we no longer need to prefetch it
                        fetcher.load('/?index&noRedirect=true&throwError=false'); // Prefetch Explore page when on People page
                        prefetchedExplore = true; // Mark Explore as prefetched after we call fetcher
                    }
                    break;
                default:
                    break;
            }
        }
    }, [location.pathname, mailboxInfo, fetcher, currentPage]);

    return (
        <>
            <AccNetworkLostToast />
            {isCurrentPageExplore ? (
                <SkipLinkRegion
                    role="main"
                    skipLinkName={loc(placesSkipToMainContent)}
                    regionName={loc(placesMainContent)}
                    className={mergeClasses(
                        isLoading && loadingBackground,
                        outletContainer,
                        exploreOutletContainer
                    )}
                >
                    <Outlet />
                </SkipLinkRegion>
            ) : (
                <SkipLinkRegion
                    role="main"
                    skipLinkName={loc(placesSkipToMainContent)}
                    regionName={loc(placesMainContent)}
                    className={mergeClasses(isLoading && loadingBackground, outletContainer)}
                >
                    <Outlet />
                </SkipLinkRegion>
            )}
            {isLoading && (
                <div className={spinner}>
                    <Spinner appearance="primary" />
                </div>
            )}
        </>
    );
}
